import { fetchWithAuth } from '../ipaService';

const API_BASE_URL = 'https://ca-ukopsportal-29zpwcp5te6-dev.bravetree-4b1a59ba.westeurope.azurecontainerapps.io';

// Types based on the API specification
export interface PaymentTrackingUISearchResultModel {
  aggregateId: string;
  externalPaymentId: string;
  direction: string;
  startTime: string;
  endTime: string;
  toCurrency: string;
  fromCurrency: string | null;
  toAmount: number;
  fromAmount: number;
  businessArea: string;
  sourceSystem: string;
  paymentMethod: string;
  paymentStatus: string;
  productCode: string;
}

export interface RealTimeStatusModel {
  totalPaymentsProcessed: number;
  psd2CriticalInTransit: number;
  suspectedDuplicate: number;
  paymentCancelled: number;
  paymentReversed: number;
  paymentReversedFailed: number;
  awaitingRepair: number;
  movedIntoWarehouse: number;
  fraudCheckTimedOut: number;
  fraudCheckHeld: number;
  fraudCheckFailed: number;
  sanctionsScreeningInitiated: number;
  sanctionsScreeningFailed: number;
  creditTransferInitiationFailed: number;
  accountBalanceHoldFailed: number;
  accountLookupFailed: number;
  accountBalanceHoldRemovalFailed: number;
  accountPostingFailed: number;
  liquidityCheckFailed: number;
}

export interface PaymentTubeMapResponseModel {
  eventId: string;
  eventName: string;
  stepName: string;
  statusTime: string | null;
  isCompleted: boolean;
  isFailed: boolean;
  stepDetails: Record<string, string>;
  children: PaymentTubeMapResponseModel[] | null;
}

export type SearchFilterType = {
  paymentTrackingUISearchType?: string;
  startSearchDateRange?: string;
  endSearchDateRange?: string;
  aggregateId?: string;
  extPaymentId?: string;
  paymentType?: 'outbound' | 'inbound';
  scheme?: string;
  messageType?: string;
  businessArea?: string;
  currency?: string;
  amount?: string;
  productCode?: string;
  sourceSystem?: string;
};

// Mock API endpoints for development
// These would be replaced with real API calls in production
const mockEndpoints = {
  searchResults: '/assets/mock/search-result.json',
  tubeMap: '/assets/mock/single-payment.json',
  dashboardStatus: '/assets/mock/api-dashboard.json',
};

export const paymentsApi = {
  // Dashboard endpoint - gets real-time payments status
  getRealTimeStatus: async (): Promise<RealTimeStatusModel> => {
    try {
      // In development, use mock data
      if (process.env.NODE_ENV === 'development') {
        const response = await fetchWithAuth(mockEndpoints.dashboardStatus);
        if (!response.ok) {
          throw new Error(`Failed to fetch dashboard data: ${response.statusText}`);
        }
        const result = await response.json();
        return result.data;
      }

      // In production, call the real API
      const response = await fetchWithAuth(`${API_BASE_URL}/v1.0/realtime-payments-status`);
      if (!response.ok) {
        throw new Error(`Failed to fetch dashboard data: ${response.statusText}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      throw error;
    }
  },

  // Search endpoint - supports filtering
  searchPayments: async (filters: SearchFilterType): Promise<PaymentTrackingUISearchResultModel[]> => {
    try {
      // Build query string from filters
      const queryParams = new URLSearchParams();
      
      // Add all non-empty filters to query params
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          queryParams.append(key, value.toString());
        }
      });

      // In development, use mock data
      if (process.env.NODE_ENV === 'development') {
        const response = await fetchWithAuth(mockEndpoints.searchResults);
        if (!response.ok) {
          throw new Error(`Failed to fetch search results: ${response.statusText}`);
        }
        const result = await response.json();
        return result.data;
      }

      // In production, call the real API
      const response = await fetchWithAuth(`${API_BASE_URL}/v1.0/payment-tracking-ui-search?${queryParams.toString()}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch search results: ${response.statusText}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error searching payments:', error);
      throw error;
    }
  },

  // Tube map endpoint - gets payment timeline
  getPaymentTimeline: async (paymentId: string, isOutbound: boolean = true): Promise<PaymentTubeMapResponseModel[]> => {
    try {
      // In development, use mock data
      if (process.env.NODE_ENV === 'development') {
        const response = await fetchWithAuth(mockEndpoints.tubeMap);
        if (!response.ok) {
          throw new Error(`Failed to fetch payment timeline: ${response.statusText}`);
        }
        const result = await response.json();
        return result.data;
      }

      // Determine the endpoint based on payment direction
      const endpoint = isOutbound 
        ? `${API_BASE_URL}/v1.0/outbound-payment-tubemap/${paymentId}`
        : `${API_BASE_URL}/v1.0/inbound-payment-tubemap/${paymentId}`;

      // In production, call the real API
      const response = await fetchWithAuth(endpoint);
      if (!response.ok) {
        throw new Error(`Failed to fetch payment timeline: ${response.statusText}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching payment timeline:', error);
      throw error;
    }
  },

  // Payment summary endpoint - gets data for PDF generation
  getPaymentSummary: async (paymentId: string): Promise<any> => {
    try {
      // In development, try to use mock data but fail gracefully
      if (process.env.NODE_ENV === 'development') {
        try {
          // Mock summary data doesn't exist yet, so this will likely fail
          const response = await fetchWithAuth(`/assets/mock/payment-summary-${paymentId}.json`);
          if (!response.ok) {
            // Return empty object if mock file doesn't exist
            return {};
          }
          const result = await response.json();
          return result.data;
        } catch (err) {
          // Return empty object if error occurs
          return {};
        }
      }

      // In production, call the real API
      const response = await fetchWithAuth(`${API_BASE_URL}/v1.0/payment-summary/${paymentId}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch payment summary: ${response.statusText}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching payment summary:', error);
      throw error;
    }
  }
}; 